import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft,
  FiCopy as Copy,
  FiCheck as Check,
  FiAlertCircle as AlertCircle,
  FiActivity as Activity,
  FiExternalLink as ExternalLink,
  FiAlertTriangle,
  FiXCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiUser as User,
  FiServer as Server
} from 'react-icons/fi';
import { getIncident, getAgentRun, acknowledgeIncident, resolveIncident } from '../../../../services/aiOperationsApi';

interface IncidentData {
  id: string;
  severity?: string;
  status?: string;
  incident_type?: string;
  affected_component?: string;
  user_impact?: string;
  affected_runs_count?: number;
  error_message?: string;
  error_stack_trace?: string;
  detected_at?: string;
  acknowledged_at?: string;
  resolved_at?: string;
  resolution_notes?: string;
  run_id?: string;
}

interface AgentRunData {
  task?: string;
  agent_name?: string;
}

const IncidentDetailPage = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<IncidentData | null>(null);
  const [parentRun, setParentRun] = useState<AgentRunData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showStackTrace, setShowStackTrace] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadIncident();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  const loadIncident = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getIncident(incidentId!);
      setIncident(data);

      // Load parent run if run_id exists
      if (data.run_id) {
        try {
          const runData = await getAgentRun(data.run_id);
          setParentRun(runData);
        } catch (err: unknown) {
          console.error('Error loading parent run:', err);
        }
      }
    } catch (err: unknown) {
      console.error('Error loading incident:', err);
      setError((err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleBack = () => {
    navigate('/ai-operations/incidents?section=incidents');
  };

  const handleAcknowledge = async () => {
    setActionLoading(true);
    try {
      await acknowledgeIncident(incidentId!);
      await loadIncident();
    } catch (err: unknown) {
      console.error('Error acknowledging incident:', err);
      alert('Failed to acknowledge incident: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveSubmit = async () => {
    if (!resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    setActionLoading(true);
    try {
      await resolveIncident(incidentId!, resolutionNotes);
      setShowResolveModal(false);
      setResolutionNotes('');
      await loadIncident();
    } catch (err: unknown) {
      console.error('Error resolving incident:', err);
      alert('Failed to resolve incident: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getSeverityColor = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getSeverityIcon = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <FiXCircle className="w-4 h-4" />;
      case 'medium':
        return <FiAlertTriangle className="w-4 h-4" />;
      case 'low':
        return <FiCheckCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'investigating':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'ignored':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getUserImpactColor = (impact: string | null | undefined) => {
    switch (impact?.toLowerCase()) {
      case 'blocked':
        return 'text-red-600 dark:text-red-400';
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'none':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Incident not found
        </div>
      </div>
    );
  }

  const canAcknowledge = incident.status === 'active';
  const canResolve = ['active', 'investigating'].includes(incident.status ?? "");

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Incidents
        </button>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Incident Details
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <code className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                {incident.id}
              </code>
              <button
                onClick={() => handleCopy(incident.id, 'id')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {copiedField === 'id' ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${getSeverityColor(incident.severity)}`}>
              {getSeverityIcon(incident.severity)}
              {incident.severity || 'Unknown'}
            </span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(incident.status)}`}>
              {incident.status || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        {(canAcknowledge || canResolve) && (
          <div className="mt-4 flex items-center gap-3">
            {canAcknowledge && (
              <button
                onClick={handleAcknowledge}
                disabled={actionLoading}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Processing...' : 'Acknowledge'}
              </button>
            )}
            {canResolve && (
              <button
                onClick={() => setShowResolveModal(true)}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                Resolve
              </button>
            )}
          </div>
        )}
      </div>

      {/* User Context Card - What the user was trying to do */}
      {parentRun?.task && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            User Context
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            What the user was trying to accomplish when this incident occurred
          </p>

          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">User Task</div>
            <p className="text-gray-900 dark:text-white">
              {parentRun.task}
            </p>
          </div>

          {parentRun.agent_name && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <User className="w-4 h-4" />
              <span>Agent: <span className="font-medium text-gray-900 dark:text-white">{parentRun.agent_name}</span></span>
            </div>
          )}
        </div>
      )}

      {/* Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Incident Type */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Incident Type</div>
            <div className="flex items-center">
              <Activity className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
              <span className="text-gray-900 dark:text-white font-medium">
                {incident.incident_type || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Affected Component */}
          {incident.affected_component && (
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Affected Component</div>
              <div className="flex items-center">
                <Server className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                <span className="text-gray-900 dark:text-white font-medium">
                  {incident.affected_component}
                </span>
              </div>
            </div>
          )}

          {/* User Impact */}
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">User Impact</div>
            <div className={`font-medium ${getUserImpactColor(incident.user_impact)}`}>
              {incident.user_impact || 'Unknown'}
            </div>
          </div>

          {/* Affected Runs */}
          {(incident.affected_runs_count ?? 0) > 0 && (
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Affected Runs</div>
              <div className="text-gray-900 dark:text-white font-medium">
                {incident.affected_runs_count}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Error Message
          </h2>
          {incident.error_message && (
            <button
              onClick={() => handleCopy(incident.error_message ?? '', 'error')}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              {copiedField === 'error' ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </button>
          )}
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <pre className="text-sm text-red-800 dark:text-red-300 whitespace-pre-wrap font-mono">
            {incident.error_message || 'No error message available'}
          </pre>
        </div>
      </div>

      {/* Stack Trace Card (Collapsible) */}
      {incident.error_stack_trace && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <button
            onClick={() => setShowStackTrace(!showStackTrace)}
            className="flex items-center justify-between w-full text-left"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Stack Trace
            </h2>
            {showStackTrace ? (
              <FiChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
          {showStackTrace && (
            <div className="mt-4 bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
                {incident.error_stack_trace}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Timeline Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Timeline
        </h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">Detected</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {formatTimestamp(incident.detected_at)}
              </div>
            </div>
          </div>

          {incident.acknowledged_at && (
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Acknowledged</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {formatTimestamp(incident.acknowledged_at)}
                </div>
              </div>
            </div>
          )}

          {incident.resolved_at && (
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Resolved</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {formatTimestamp(incident.resolved_at)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resolution Notes Card */}
      {incident.resolution_notes && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Resolution Notes
          </h2>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-300 whitespace-pre-wrap">
              {incident.resolution_notes}
            </p>
          </div>
        </div>
      )}

      {/* Related Information Card */}
      {(incident.run_id || parentRun) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Related Information
          </h2>
          <div className="space-y-4">
            {incident.run_id && (
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Agent Run</div>
                  <code className="text-sm text-gray-900 dark:text-white font-mono">
                    {incident.run_id}
                  </code>
                  {parentRun && (
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Agent: {parentRun.agent_name}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/ai-operations/traces/${incident.run_id}`}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                  >
                    View Trace
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/ai-operations/runs/${incident.run_id}`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    View Run
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Resolve Incident
            </h3>
            <div className="mb-4">
              <label htmlFor="resolution-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resolution Notes
              </label>
              <textarea id="resolution-notes"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe how this incident was resolved..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowResolveModal(false);
                  setResolutionNotes('');
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveSubmit}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-md
                         hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Resolving...' : 'Resolve Incident'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentDetailPage;
